import clsx from 'clsx';
import React, { type PropsWithChildren, useCallback } from 'react';

import type {
  TWGapProp,
  TWHeightProp,
  TWPaddingProp,
} from '../../types/tailwind';

type Option<T extends string> = { label: string; value: T };

type TabProps<T extends string, PaddingProp, HeightProp> = Pick<
  Option<T>,
  'value'
> & {
  selected?: boolean;
  onClick: (value: T) => void;
  padding?: TWPaddingProp<PaddingProp>;
  height?: TWHeightProp<HeightProp>;
};

const Tab = <T extends string, PaddingProp, HeightProp>({
  children,
  value,
  onClick,
  selected = false,
  padding = 'py-f1 px-[12px]' as TWPaddingProp<PaddingProp>,
  height,
}: PropsWithChildren<TabProps<T, PaddingProp, HeightProp>>) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    className={clsx(
      'flex items-center',
      selected ? 'bg-main-200' : 'bg-transparent',
      height,
      padding,
      selected ? 'text-main' : 'text-main-500',
      'hover:text-main hover:cursor-pointer border-[0.5px] border-main-300'
    )}
    data-testid={value}
  >
    {children}
  </button>
);

type TabsProps<T extends string, PaddingProp, HeightProp, GapProp> = {
  options: Option<T>[] | readonly Option<T>[];
  selectedValue?: T;
  onClick: (val: T) => void;
  extraClassNames?: string;
  'data-testid'?: string;
  padding?: TWPaddingProp<PaddingProp>;
  height?: TWHeightProp<HeightProp>;
  gap?: TWGapProp<GapProp>;
};

export const Tabs = <T extends string, PaddingProp, HeightProp, GapProp>({
  options,
  padding = 'p-0' as TWPaddingProp<PaddingProp>,
  'data-testid': dataTestId,
  selectedValue = undefined,
  extraClassNames,
  height,
  gap = 'gap-[1ch]' as TWGapProp<GapProp>,
  onClick,
}: TabsProps<T, PaddingProp, HeightProp, GapProp>) => {
  const onClickHandler = useCallback(
    (value: T) => {
      onClick(value);
    },
    [onClick]
  );
  return (
    <div
      data-testid={dataTestId}
      className={clsx(
        'flex',
        'overflow-auto',
        'scrollbar-hide',
        gap,
        extraClassNames
      )}
    >
      {options.map((opt) => (
        <Tab
          key={opt.value}
          height={height}
          padding={padding}
          value={opt.value}
          onClick={onClickHandler}
          selected={opt.value === selectedValue}
        >
          {opt.label}
        </Tab>
      ))}
    </div>
  );
};
